<script>
  // Step 2 body: Find Frames. Renders inside a <StepCard>; the parent
  // panel owns the card's number/title/subline/done/warning.
  import { createEventDispatcher, getContext } from "svelte";

  import { Modal } from "svelte-utilities";
  import { api } from "#src/api";
  import { user } from "#src/stores/auth.js";
  import { currentTool, selectedLocations, selectedPages } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";
  import { isScaled, sheetFrames } from "../derive.js";
  import { sheetNumber } from "../sheet-label.js";
  import { plural } from "../labels.js";
  import { locateFramesOnSheet } from "../locate.js";
  import { selectAllIds } from "../selection.js";
  import { forceScaleFor } from "../force-scale.js";
  import ScaleWarning from "../ScaleWarning.svelte";

  export let group;
  export let doc;
  export let pages;
  export let indexedLocations;
  export let visibleIndices;
  export let selectedPageList;
  export let collections;
  export let viewer;
  /** The live canvas frame selection, derived once by FrameXPanel. */
  export let selectedLocationList;
  /** Frames across the whole working set — gates the continue control only. */
  export let frameCount = 0;
  /** Pages in the FrameX working set, in document order. */
  export let workingIds = [];

  const dispatch = createEventDispatcher();
  const org = getContext("org");

  let running = false;
  let scopeModal;
  let scopeChoice = "all";
  // Sheet-by-sheet progress. The locator is one detector call per sheet, run
  // in series, so "all sheets" on a large job is minutes long — the button has
  // to say where it is rather than just that it is busy.
  let progress = null;

  // Names a page for the error alert. The sheet number off the title block is how
  // drafters identify a page, so it wins when there is one; a page without one
  // falls back to its position, the way the chips do. The list form moved to
  // ScaleWarning along with the strip it served, leaving the standalone form,
  // where "page #3" marks 3 as a reference to one page rather than a count.
  $: pageName = (id) => sheetNumber(pages[id]) || `page #${visibleIndices[id] + 1}`;

  // The locator runs on exactly the sheets selected on canvas — never on the
  // whole working set. A sheet without a scale cannot be located, and setting
  // its scale is also what admits it to FrameX, so the unscaled case is a
  // prompt rather than a silent skip.
  $: selectedPageIds = selectedPageList.map((p) => p.id);
  // Listed in page order, never selection order: the warning is read against
  // the drawing set, so "pages 2, 3, and 4" has to match the sequence on
  // screen rather than the order they happened to be clicked. Sorting the
  // filtered copy — selectedPageList is the caller's array and stays untouched.
  $: unscaledSelected = selectedPageIds
    .filter((id) => !isScaled(pages[id]))
    .sort((a, b) => visibleIndices[a] - visibleIndices[b]);
  $: locatableIds = selectedPageIds.filter((id) => isScaled(pages[id]));

  // detectDone alone, NOT hasBeenLocated. hasBeenLocated is
  // `detectDone || has frames`, which conflates two different facts — right
  // for the step's done-state, wrong here. Keyed on the flag, the count is
  // explainable: a page searched that found nothing is in the "already run"
  // group, so 0 means the detector has been everywhere rather than something
  // the user cannot account for. It also puts pages with hand-drawn frames
  // but no detector run back in scope, which is where running it helps.
  //
  // The flag is reliable in both directions: locate.js sets it after the loop
  // even on zero finds, and a page whose detection throws never reaches that
  // line, so a failed page correctly stays "not yet run".
  $: unsearchedIds = workingIds.filter((id) => !pages[id]?.detectDone);
  // Every sheet in FrameX is scaled by definition (membership is isScaled &&
  // !notApplicable && !framexExcluded), so "all" and "not yet searched" can
  // never contain a sheet the locator would choke on. Only "selected" can,
  // which is why the force-scale divert below still runs first.
  //
  // A scaled sheet marked N/A is selectable but not a member, so the selected
  // count can be non-zero while the working set is empty — the dialog has
  // something to offer in that case and must not show its empty state.
  $: nothingToRun = workingIds.length === 0 && locatableIds.length === 0;

  // Frames on the selected sheets, or a frame selection standing on its own. Page
  // and frame selections are mutually exclusive — RightPanelTabs clears one when
  // the other is set — so only one term is ever non-zero, and the OR just means
  // "whichever selection is live". Both terms are needed: Find Frames ends by
  // selecting the frames it created, which clears the page selection, so without
  // the second term the guidance would vanish the instant the frames appeared.
  $: selectedFrameCount = selectedPageIds.reduce((n, id) => n + sheetFrames(indexedLocations[id]).length, 0);
  $: showAdjustHint = selectedFrameCount > 0 || selectedLocationList.length > 0;

  // While the frame tool is armed the secondary button reflects it, and the
  // helper line below it swaps to the armed instruction. This is the only
  // draw-mode affordance now — the canvas hint banner it used to mirror is
  // gone (see DrawHintBanner.svelte).
  $: drawing = $currentTool === "location-collection";

  // Ignoring the scale warning must not quietly locate the scaled half of the
  // selection and skip the rest — that was the bug. Route through the same
  // forced-scale flow a blocked frame placement uses, so the demand is a modal
  // rather than a strip the user already scrolled past.
  //
  // The selection narrows to the pages that need a scale first: Step 1's modal
  // applies to the whole selection, so leaving the scaled pages in it would
  // overwrite their scales. Same move Document.svelte makes with selectOnly on
  // the placement path.
  function handleFind() {
    if (unscaledSelected.length) {
      selectedPages.set(selectAllIds(unscaledSelected));
      forceScaleFor.set({
        pageId: unscaledSelected[0],
        note:
          unscaledSelected.length === 1
            ? "This page needs a scale before frames can be located on it."
            : "These pages need a scale before frames can be located on them.",
      });
      return;
    }
    openScopeDialog();
  }

  // Narrowest scope that is not empty, resolved when the dialog opens rather
  // than tracked reactively — a selection made while it is open must not move
  // the radio out from under the user. Same rule as step 3's openScopeDialog.
  function openScopeDialog() {
    scopeChoice =
      locatableIds.length > 0
        ? "selected"
        : unsearchedIds.length > 0 && unsearchedIds.length < workingIds.length
          ? "unsearched"
          : "all";
    scopeModal.open();
  }

  function scopeSheets(choice) {
    if (choice === "selected") return locatableIds;
    if (choice === "unsearched") return unsearchedIds;
    return workingIds;
  }

  function confirmFind() {
    runLocator(scopeSheets(scopeChoice));
  }

  async function runLocator(ids) {
    if (running || !ids.length) return;
    running = true;

    const { data } = await api.auth.getSession();
    const token = data?.session?.access_token;

    let added = 0;
    let failed = 0;
    // One list for the whole command. The locator runs a sheet at a time, and
    // every sheet's frames append here rather than being selected as they land,
    // so the run ends with all of them selected together instead of each batch
    // replacing the last.
    const newIds = [];

    for (const [i, pageId] of ids.entries()) {
      progress = { done: i, total: ids.length };
      try {
        const result = await locateFramesOnSheet({
          pageId,
          doc,
          group,
          groupId: $group.id,
          user: $user,
          org: $org,
          collections,
          indexedLocations,
          token,
          viewer,
          visibleIndices,
        });
        added += result.added;
        newIds.push(...result.locationIds);
      } catch (err) {
        failed += 1;
        alerts.add({
          type: "error",
          message: `Couldn't locate frames on ${pageName(pageId)}: ${err.message}`,
        });
      }
    }

    running = false;
    progress = null;

    // Select everything this command created, so the new frames' boundaries are
    // on screen without the user hunting for them. One write, after every sheet
    // has returned: each sheet's run calls group.update twice, and several
    // Document.svelte handlers clear $selectedLocations, so writing mid-run would
    // sit inside that window. Guarded on length so a run that found nothing
    // leaves the existing selection alone instead of silently clearing it.
    if (newIds.length) selectedLocations.set(selectAllIds(newIds));

    if (added > 0) {
      alerts.add({ type: "ok", message: `Found ${added} frame${plural(added)}.` });
    } else if (!failed) {
      alerts.add({ type: "info", message: "No new frames found." });
    }
  }

  // Arms the app's existing Add Frame Marker tool, and disarms it again — the
  // button is the same control both ways, so the way out is where the way in
  // was. Escape still works and is unchanged.
  //
  // The disarm sets `$currentTool` and nothing else, which is exactly what
  // handleEscape does to the tool: Document.svelte's checkCurrentTool clears
  // `adding` off that change on its own. Deliberately NOT a dispatched event or
  // a shared disarm handler — an earlier "Done drawing" toggle routed through
  // one of those and broke box completion.
  function toggleManual() {
    if (drawing) {
      $currentTool = "select";
      return;
    }
    $currentTool = "location-collection";
    dispatch("click-location-tool");
  }
</script>

<div class="fx-actions">
  <ScaleWarning {pages} {selectedPageList} {visibleIndices} action="frames can be located" />
  <button class="fx-primary-button" disabled={running} on:click={handleFind}>
    <!-- Never disabled for want of a selection: the dialog offers whole-set
         scopes, so there is always something to choose. The ellipsis marks a
         control that opens a dialog, matching step 3's "Build Frames…". While
         running the label reports which sheet it is on — one detector call per
         sheet, in series, so a bare "Locating…" would look hung on a long
         run. -->
    {running
      ? progress
        ? `Locating page ${progress.done + 1} of ${progress.total}…`
        : "Locating…"
      : "Find Frames…"}
  </button>

  <!-- The found-count itself lives on the step row, which states it whether or
       not this row is open — repeating it here read as two separate numbers.
       What stays is the guidance, shown only when the live selection actually
       puts a frame in front of you. -->
  {#if showAdjustHint}
    <div class="fx-found">
      <span class="fx-found-title">Adjust frame boundaries.</span>
      <span class="fx-found-copy">
        Drag corners so the entire detail is inside, including mark and dimensions.
      </span>
    </div>
  {/if}

  <!-- Drawing by hand needs no canvas selection: the tool is canvas-wide and
       any scaled page accepts a frame. -->
  <button class="fx-secondary-button" class:fx-draw-active={drawing} on:click={toggleManual}>
    {drawing ? "Done adding" : "Add manually"}
  </button>
  <!-- Only while armed. At rest the button already says what it does, and a
       standing drag instruction described an action that was not running. -->
  {#if drawing}
    <div class="fx-hint">Draw a box around each glazing detail including its mark and dimensions.</div>
  {/if}
  <!-- Gated on frames existing, not on step 2 being "done": a sheet the
       detector came up empty on is legitimately finished, but there is
       nothing there to go and build. -->
  {#if frameCount > 0}
    <button class="fx-continue" on:click={() => dispatch("continue-to-build")}>
      Continue to Build Frames →
    </button>
  {/if}
</div>

<Modal
  bind:this={scopeModal}
  closeable
  closeOnOutclick
  on:confirm={confirmFind}
  buttons={nothingToRun
    ? [{ label: "Close", type: "cancel" }]
    : [
        { label: "Cancel", type: "cancel" },
        { label: "Find Frames", type: "confirm", style: "primary" },
      ]}>
  <div slot="title">Find Frames</div>
  <div slot="content" class="fx-scope-options">
    {#if nothingToRun}
      <!-- Three dead radios would be the disabled button again, moved. When
           there is genuinely nothing to run on, say what would change that. -->
      <div class="fx-hint">No pages are in FrameX yet — set a page scale in step 1 to add one.</div>
    {:else}
      <label class="fx-scope-option" class:fx-scope-selected={scopeChoice === "all"}>
        <input type="radio" bind:group={scopeChoice} value="all" disabled={workingIds.length === 0} />
        <span>All pages in FrameX ({workingIds.length}) — searches every page again</span>
      </label>
      <label
        class="fx-scope-option"
        class:fx-scope-selected={scopeChoice === "unsearched"}
        class:fx-scope-disabled={unsearchedIds.length === 0}>
        <input
          type="radio"
          bind:group={scopeChoice}
          value="unsearched"
          disabled={unsearchedIds.length === 0} />
        <span
          >Only pages the detector hasn't run on yet ({unsearchedIds.length}) — skip pages already searched</span>
      </label>
      <label
        class="fx-scope-option"
        class:fx-scope-selected={scopeChoice === "selected"}
        class:fx-scope-disabled={locatableIds.length === 0}>
        <input type="radio" bind:group={scopeChoice} value="selected" disabled={locatableIds.length === 0} />
        <span>Only the pages you've selected ({locatableIds.length})</span>
      </label>
    {/if}
  </div>
</Modal>

<style lang="scss">
  // The shared .fx-secondary-button carries the shape; this is only the armed
  // state. Deliberately NOT the handoff's drawMode orange (#f0983c / #c26a12):
  // framex.scss already rejected the prototype's orange for hand-tagged frames
  // in favour of --fx-manual, and the button that arms hand-tagging has to
  // match the frames it produces — two different oranges for one concept is
  // what this replaced. The border is the fill those markers carry; the text
  // takes the palette's warm tone, since --fx-manual is far too light to read
  // as type.
  .fx-draw-active {
    border-color: var(--fx-manual);
    color: var(--fx-amber-text);
  }

  // The same strip as ScaleWarning's — same box, same amber tokens — so the two
  // read as one kind of object in this step. The text colour sits here and the
  // copy inherits it, and no line-height is declared, matching that rule exactly.
  .fx-found {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 8px;
    border-radius: 4px;
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
  }

  // The lead line names the task, the copy says how. Same size and colour as the
  // copy — weight alone separates them, so the strip still reads as one object.
  .fx-found-title {
    font-size: 12px;
    font-weight: 600;
  }

  .fx-found-copy {
    font-size: 12px;
  }
</style>
